import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { AccessTime, AttachMoney, Description } from '@mui/icons-material';
import { caretakerProfileService } from '../services/caretakerProfileService';

interface Service {
  id: string;
  name: string;
  description?: string | null;
  duration?: number | null;
  price?: number | string | null;
}

interface ServiceSelectionProps {
  caretakerId: string;
  onServiceSelect: (service: Service) => void;
  onError: (error: string) => void;
}

const ServiceSelection: React.FC<ServiceSelectionProps> = ({
  caretakerId,
  onServiceSelect,
  onError,
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const caretakerProfile = await caretakerProfileService.getCaretakerProfile(caretakerId);
        const profileServices =
          'services' in caretakerProfile ? caretakerProfile.services : [];
        setServices(profileServices || []);
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Failed to load services');
      } finally {
        setLoading(false);
      }
    };

    if (caretakerId) {
      fetchServices();
    }
  }, [caretakerId, onError]);

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
  };

  const handleContinue = () => {
    if (selectedService) {
      onServiceSelect(selectedService);
    }
  };

  const formatPrice = (price?: number | string | null) => {
    if (!price) return 'Price not set';
    // Convert to number if it's a string (API might return string instead of number)
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'Price not set';
    return `$${numericPrice.toFixed(2)}`;
  };

  const formatDuration = (duration?: number | null) => {
    if (!duration) return 'Duration not set';
    if (duration < 60) return `${duration} minutes`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (services.length === 0) {
    return (
      <Alert severity="info">
        No services available for this caretaker. Please contact them directly to discuss available services.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Service
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose the service you'd like to book with this caretaker.
      </Typography>

      <Grid container spacing={2}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} key={service.id}>
            <Card
              sx={{
                cursor: 'pointer',
                border: selectedService?.id === service.id ? 2 : 1,
                borderColor: selectedService?.id === service.id ? 'primary.main' : 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
              onClick={() => handleServiceSelect(service)}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {service.name}
                </Typography>
                
                {service.description && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Description sx={{ mr: 1, mt: 0.5, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {service.duration && (
                    <Chip
                      icon={<AccessTime />}
                      label={formatDuration(service.duration)}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {service.price && (
                    <Chip
                      icon={<AttachMoney />}
                      label={formatPrice(service.price)}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </Box>

                {selectedService?.id === service.id && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleContinue}
                    >
                      Continue with {service.name}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {selectedService && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{ minWidth: 200 }}
          >
            Continue with {selectedService.name}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ServiceSelection;
